import { useCallback, useEffect, useReducer } from 'react';
import { fetchBusinessEmails } from '~/api';
import { useSelector } from '~/store';
import { selectBusinessUuid } from '~/store/slices/formSlice/selectors';
import { ApiResponseEnum, apiResponseHandler } from '~/utils/api';
import { IListEmail } from '../constants';

interface IBusinessEmailApiResponse {
  next: string | null;
  previous: string | null;
  count: number;
  total_pages: number;
  results: IListEmail[];
}

interface State {
  listIds: string[];
  listData: Record<string, IListEmail>;
  isLoading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalCount: number;
}

const initialState: State = {
  listIds: [],
  listData: {},
  isLoading: false,
  error: null,
  currentPage: 1,
  totalPages: 1,
  totalCount: 0,
};

type Action =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; payload: { emails: IBusinessEmailApiResponse; page: number } }
  | { type: 'FETCH_FAILURE'; error: string }
  | { type: 'RESET_LIST' };

const emailReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, isLoading: true, error: null };

    case 'FETCH_SUCCESS':
      const { emails, page } = action.payload;
      const newIds = emails.results.map(email => email.email_uuid);
      const newData = emails.results.reduce<Record<string, IListEmail>>((acc, email) => {
        acc[email.email_uuid] = email;
        return acc;
      }, {});

      return {
        ...state,
        isLoading: false,
        listIds: newIds,
        listData: newData,
        currentPage: page,
        totalPages: emails.total_pages,
        totalCount: emails.count,
      };

    case 'FETCH_FAILURE':
      return { ...state, isLoading: false, error: action.error };

    case 'RESET_LIST':
      return { ...initialState };

    default:
      return state;
  }
};

export const useEmailList = (shouldFetch = true) => {
  const [state, dispatch] = useReducer(emailReducer, initialState);
  const businessUuid = useSelector(selectBusinessUuid);

  const fetchEmails = useCallback(
    async (page = 1) => {
      dispatch({ type: 'FETCH_START' });

      const promise = fetchBusinessEmails(businessUuid, page);
      const response = await apiResponseHandler<IBusinessEmailApiResponse>(promise);

      if (response.type === ApiResponseEnum.Success) {
        dispatch({ type: 'FETCH_SUCCESS', payload: { emails: response.data, page } });
      } else {
        dispatch({ type: 'FETCH_FAILURE', error: response.error });
      }
    },
    [businessUuid],
  );

  useEffect(() => {
    if (shouldFetch) {
      fetchEmails(state.currentPage);
    }
  }, [shouldFetch, businessUuid]);

  return {
    ...state,
    fetchNextPage: () => {
      if (state.currentPage < state.totalPages) {
        fetchEmails(state.currentPage + 1);
      }
    },
    fetchSpecificPage: (page: number) => {
      if (page >= 1 && page <= state.totalPages) {
        fetchEmails(page);
      }
    },
    resetList: () => dispatch({ type: 'RESET_LIST' }),
  };
};
